import type { ChangeEvent, FC } from 'react';
import { useCallback, useState } from 'react';
import { Button, Card, Stack, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Iconify,
  LoadingPromiseButtonAdvanced,
  Modal,
  useErrorSnackbar,
} from '@pflegenavi/web-components';
import { useTranslation } from 'react-i18next';
import type { KYCDocument } from '@pflegenavi/service-provider-frontend/api';
import { useCreateKycDocument } from '@pflegenavi/service-provider-frontend/api';
import { isDefined } from '@pflegenavi/shared/utils';
import { ClientError } from '@pflegenavi/shared-frontend/platform';
import { enqueueSnackbar } from 'notistack';
import { v4 as uuid } from 'uuid';

interface FileUpload {
  key: string;
  file: File | undefined;
  base64: string | undefined;
}

interface UploadDocumentsModalViewProps {
  open: boolean;
  handleClose: () => void;
  type: KYCDocument['Type'];
  legalUserType: 'BUSINESS' | 'ORGANIZATION' | 'SOLETRADER' | 'PARTNERSHIP';
  files: FileUpload[];
  onFileRead: (key: string, event: ChangeEvent<HTMLInputElement>) => void;
  onAddFile: () => void;
  onRemoveFile: (key: string) => void;
  onSubmit: () => Promise<void>;
  isSubmitDisabled: boolean;
}

export const UploadDocumentsModalView: FC<UploadDocumentsModalViewProps> = ({
  open,
  handleClose,
  type,
  legalUserType,
  files,
  onFileRead,
  onAddFile,
  onRemoveFile,
  onSubmit,
  isSubmitDisabled,
}) => {
  const { t } = useTranslation();

  const uploadDescription = (() => {
    if (type === 'IDENTITY_PROOF') {
      if (legalUserType === 'BUSINESS') {
        return t('kyc.identity.business');
      }
      if (legalUserType === 'ORGANIZATION') {
        return t('kyc.identity.organization');
      }
      if (legalUserType === 'SOLETRADER') {
        return t('kyc.identity.soletrader');
      }
    }
    if (type === 'REGISTRATION_PROOF') {
      if (legalUserType === 'BUSINESS') {
        return t('kyc.registration-proof.business');
      }
      if (legalUserType === 'ORGANIZATION') {
        return t('kyc.registration-proof.organization');
      }
      if (legalUserType === 'SOLETRADER') {
        return t('kyc.registration-proof.soletrader');
      }
    }
    if (type === 'ARTICLES_OF_ASSOCIATION') {
      if (legalUserType === 'BUSINESS') {
        return t('kyc.articles-of-association.business');
      }
      if (legalUserType === 'ORGANIZATION') {
        return t('kyc.articles-of-association.organization');
      }
    }
    if (type === 'SHAREHOLDER_DECLARATION') {
      if (legalUserType === 'BUSINESS') {
        return t('kyc.shareholder-declaration.business');
      }
    }
    return '';
  })();

  return (
    <Modal open={open} handleClose={handleClose}>
      <Card
        data-testid="upload-documents-modal"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          p: 3,
          gap: 2,
          maxWidth: 500,
        }}
      >
        <Typography variant="h4">{t('kyc.please-upload-documents')}</Typography>
        <Typography variant="subtitle2">{uploadDescription}</Typography>
        <Stack
          sx={{
            gap: 1,
          }}
        >
          {files.map((file) => (
            <Stack
              key={file.key}
              direction="row"
              sx={{
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Button
                data-testid="upload-file-button"
                component="label"
                role={undefined}
                variant="contained"
                tabIndex={-1}
                startIcon={<Iconify icon="eva:upload-fill" />}
              >
                {t('kyc.upload-file')}
                <VisuallyHiddenInput
                  data-testid="upload-file-input"
                  type="file"
                  accept="image/*, .pdf"
                  onChange={(event) => onFileRead(file.key, event)}
                />
              </Button>
              {file.file && (
                <Typography data-testid="uploaded-file-name" variant="body1">
                  {file.file.name}
                </Typography>
              )}
              {files.length > 1 && (
                <Button onClick={() => onRemoveFile(file.key)}>
                  <Iconify icon="eva:trash-2-outline">
                    {t('kyc.remove-file')}
                  </Iconify>
                </Button>
              )}
            </Stack>
          ))}
          <Stack
            direction="row"
            sx={{
              justifyContent: 'flex-end',
            }}
          >
            <Button
              data-testid="add-file-button"
              onClick={onAddFile}
              disabled={files.length > 0 && !files[files.length - 1].file}
              startIcon={<Iconify icon="eva:plus-fill" />}
            >
              {t('kyc.add-file')}
            </Button>
          </Stack>
        </Stack>

        <Stack
          direction="row"
          sx={{
            justifyContent: 'flex-end',
          }}
        >
          <LoadingPromiseButtonAdvanced
            data-testid="submit-documents-button"
            onClick={onSubmit}
            disabled={isSubmitDisabled}
            variant="contained"
          >
            {t('kyc.submit')}
          </LoadingPromiseButtonAdvanced>
        </Stack>
      </Card>
    </Modal>
  );
};

interface UploadDocumentsModalProps {
  open: boolean;
  handleClose: () => void;
  type: KYCDocument['Type'];
  legalUserType: 'BUSINESS' | 'ORGANIZATION' | 'SOLETRADER' | 'PARTNERSHIP';
}

export const UploadDocumentsModal: FC<UploadDocumentsModalProps> = ({
  open,
  handleClose,
  type,
  legalUserType,
}) => {
  const { t } = useTranslation();
  const { enqueueErrorSnackbar } = useErrorSnackbar();
  const { mutateAsync: createKycDocument } = useCreateKycDocument();

  const [files, setFiles] = useState<FileUpload[]>([
    {
      key: 'initial',
      file: undefined,
      base64: undefined,
    },
  ]);

  const submit = useCallback(async () => {
    try {
      await createKycDocument({
        body: {
          Type: type,
          pages: files.map((file) => file.base64).filter(isDefined),
        },
      });
      enqueueSnackbar({
        variant: 'success',
        message: t('kyc.identity-submission-successful'),
      });
    } catch (err) {
      if (err instanceof ClientError && err.statusCode === 413) {
        enqueueErrorSnackbar(err, t('kyc.identity-submission-too-big'));
      } else {
        enqueueErrorSnackbar(err, t('errors.something-went-wrong'));
      }
    }
    handleClose();
  }, [createKycDocument, enqueueErrorSnackbar, files, handleClose, t, type]);

  const handleFileRead = useCallback(
    async (key: string, event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0] as File;
      const base64 = await convertBase64(file);

      setFiles(files.map((f) => (f.key === key ? { ...f, file, base64 } : f)));
    },
    [files]
  );

  const addFile = useCallback(() => {
    setFiles((files) => [
      ...files,
      { key: uuid(), file: undefined, base64: undefined },
    ]);
  }, []);

  const removeFile = useCallback((key: string) => {
    setFiles((files) => files.filter((f) => f.key !== key));
  }, []);

  const isSubmitDisabled = files.length === 0 || !files[files.length - 1].file;

  return (
    <UploadDocumentsModalView
      open={open}
      handleClose={handleClose}
      type={type}
      legalUserType={legalUserType}
      files={files}
      onFileRead={handleFileRead}
      onAddFile={addFile}
      onRemoveFile={removeFile}
      onSubmit={submit}
      isSubmitDisabled={isSubmitDisabled}
    />
  );
};

const convertBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const fileReader = new FileReader();
    fileReader.readAsDataURL(file);
    fileReader.onload = () => {
      const base64 = (fileReader.result as string).split(',')[1];
      resolve(base64);
    };
    fileReader.onerror = (error) => {
      reject(error);
    };
  });
};

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});
